import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import isequal from 'lodash.isequal';

import { getDisplayName } from 'components/hoc';
import { areSequencesDisabled, FLAGS as EnvFlags } from 'defs/flags';
import { transformSequenceStateToFlag } from 'defs/sequences/utils';
import { SequenceList, SequenceState } from 'defs/sequences/utils';
import { group, groupEnd, log, warn } from 'helper/debug';
import { actionCreators as sequenceAC, getSequencesByName } from 'store/sequences';

if (areSequencesDisabled) {
	warn(`Sequences are currently disabled by ${EnvFlags.SEQUENCES} ...`);
}

export type WithSequenceStatePropsType = {
	sequences: { [key: string]: string };
	isSingleSequenceLoading: { [key: string]: boolean };
	isSequenceLoading: boolean;
};

/*
 * Utility to auto-map sequence states to Components anywhere on
 * the page. It doesn't matter if they're below the root or
 * over 20 layers down inside the page.
 *
 * @version 1.0.0
 * @author Jan Biasi <biasijan@gmail.com>
 * @param {React.Component} WrappedComponent    Component which should listen to sequences
 * @param {Array<string>} sequences             Sequences stored under defs/sequences
 */
export default function withSequenceState<P extends object>(
	WrappedComponent: React.ComponentType<P>,
	sequences: SequenceList = []
) {
	class SequencedStateProxy extends React.Component<P & WithSequenceStatePropsType> {
		public static displayName = `WithSequenceState(${getDisplayName(WrappedComponent)})`;
		public static sequenceCycleCount = 0;
		/*
		 * Check if the new sequences matches the following criterias:
		 *  - is enabled by the sequences definition (param#2)
		 *  - the current sequence state ...
		 *      - ... is not available (on start)
		 *      - ... is not the same (on change)
		 *
		 * @param {Object} newSequence      The new sequence
		 * @returns {Array<string>}         Changed keys if available
		 */
		public detectAnySequenceChanges(newSequence): string[] {
			SequencedStateProxy.sequenceCycleCount++;
			const currentSequence = this.props.sequences || [];
			const newSequenceKeys = Object.keys(newSequence);
			const changedKeys: string[] = [];

			newSequenceKeys.forEach((key: string) => {
				if (~sequences.indexOf(key) && (!currentSequence[key] || currentSequence[key] !== newSequence[key])) {
					group(`Sequence(${key}) [${SequencedStateProxy.sequenceCycleCount}]`);
					log(`Optimistic updates: ${newSequenceKeys.join(', ')}`);
					log(`Previous State: ${currentSequence[key]}\nNext State: ${newSequence[key]}`);
					groupEnd(`Sequence(${key}) [${SequencedStateProxy.sequenceCycleCount}]`);
					changedKeys.push(key);
				}
			});

			return changedKeys.filter((sequence) => (typeof sequence === 'string' ? sequence : void 0));
		}

		public hasPropsChanged(nextProps) {
			return !isequal(this.props, nextProps);
		}

		/*
		 * This component will be relevant for the performance of
		 * all wrapped components. Check the hook if any issues appear.
		 * @param {Object} nextProps
		 */

		public shouldComponentUpdate(nextProps): boolean {
			if (this.hasPropsChanged(nextProps)) {
				return true;
			}

			if (sequences.length === 0 || areSequencesDisabled) {
				return false;
			}

			if (this.detectAnySequenceChanges(nextProps.sequences).length === 0) {
				return false;
			}

			return true;
		}

		/*
		 * Transforms the current sequence states to an object map with
		 * the type Object<string, boolean> instead of displaying the state-def
		 * @return {Object}
		 */

		public transformSequenceStates() {
			const { sequences: sequenceProps } = this.props;
			const loadingStates = {};

			// tslint:disable-next-line: forin
			for (const sequenceName in sequenceProps) {
				const sequenceState: SequenceState = sequenceProps[sequenceName] as any;
				loadingStates[sequenceName as any] = transformSequenceStateToFlag(sequenceState);
			}

			return loadingStates;
		}

		/*
		 * Check if any of the passed sequences has a loading state
		 * @param { Object<string, boolean>} sequenceStates      The sequences states
		 */
		public isAnySequenceLoading(sequenceStates): boolean {
			let isAnyLoading = false;

			for (const sequenceName in sequenceStates) {
				if (sequenceStates[sequenceName]) {
					isAnyLoading = true;
				}
			}

			return isAnyLoading;
		}

		public render() {
			const transformedSequences = this.transformSequenceStates();
			const isAnySequenceLoading = this.isAnySequenceLoading(transformedSequences);
			const validProps: P = {
				...this.props,
				doFinishSequence: undefined,
				doFlushSequences: undefined,
				doStartSequence: undefined,
			};

			return (
				<WrappedComponent
					{...validProps}
					{...this.state}
					isSingleSequenceLoading={transformedSequences}
					isSequenceLoading={isAnySequenceLoading}
				/>
			);
		}
	}

	const mapStateToProps = (state) => ({
		sequences: getSequencesByName(state, sequences),
	});

	const mapDispatchToProps = (dispatch) => {
		return bindActionCreators({ ...sequenceAC }, dispatch);
	};

	return connect(mapStateToProps, mapDispatchToProps)(SequencedStateProxy as any);
}
